"use client";

import { Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import nextDynamic from "next/dynamic";
import { Typography } from "@/components/common/Typography";
import { BookOpenIcon, BuildingIcon, LockIcon } from "@/components/ui/icons";

const DynamicLoginForm = nextDynamic(
  () =>
    import("@/components/auth/LoginForm").then((mod) => ({
      default: mod.default,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="rounded-[2rem] border border-white/70 bg-white/90 p-10 text-center shadow-[0_30px_80px_-35px_rgba(15,23,42,0.45)] backdrop-blur-xl">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
        <Typography className="mt-4 text-sm font-medium text-slate-500">
          Loading secure login...
        </Typography>
      </div>
    ),
  },
);

const loginHighlights = [
  {
    title: "Learning dashboard",
    description:
      "Resume courses, lessons, and progress from one calm workspace.",
    icon: BookOpenIcon,
    iconWrapClassName: "bg-emerald-100 text-emerald-600",
  },
  {
    title: "Institution tools",
    description:
      "Move into admin and classroom workflows without extra friction.",
    icon: BuildingIcon,
    iconWrapClassName: "bg-blue-100 text-blue-600",
  },
] as const;

function LoginEntryPageContent() {
  const searchParams = useSearchParams();
  const error = searchParams.get("error");
  const tLogin = useTranslations("login");

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-primary/10 via-blue-50 to-indigo-100">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 right-0 h-72 w-72 rounded-full bg-primary/15 blur-3xl"></div>
        <div className="absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-sky-200/60 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-200/40 blur-3xl"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-start px-4 py-8 sm:px-6 lg:items-center lg:px-8 lg:py-12">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <section className="order-2 min-w-0 flex flex-col justify-center lg:order-1 lg:pr-6">
            <div className="max-w-2xl min-w-0">
              <Typography
                variant="h1"
                className="mt-8 text-4xl font-bold leading-tight text-slate-900 lg:text-5xl"
              >
                Welcome back.
              </Typography>

              <div className="mt-5 max-w-xl space-y-2 text-lg leading-8 text-slate-600">
                <Typography className="text-lg leading-8 text-slate-600">
                  As a student you can access your courses.
                </Typography>
                <Typography className="text-lg leading-8 text-slate-600">
                  As an institution you can control your institution.
                </Typography>
                <Typography className="text-lg leading-8 text-slate-600">
                  As a teacher you can manage your classes and students.
                </Typography>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {loginHighlights.map(
                  ({ title, description, icon: Icon, iconWrapClassName }) => (
                    <div
                      key={title}
                      className="rounded-2xl border border-white/80 bg-white/65 p-5 shadow-lg shadow-slate-200/40 backdrop-blur-sm"
                    >
                      <div
                        className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${iconWrapClassName}`}
                      >
                        <Icon size={20} />
                      </div>
                      <Typography
                        variant="large"
                        className="text-base font-semibold text-slate-900"
                      >
                        {title}
                      </Typography>
                      <Typography className="mt-2 text-sm leading-6 text-slate-600">
                        {description}
                      </Typography>
                    </div>
                  ),
                )}
              </div>
            </div>
          </section>

          <section className="order-1 min-w-0 flex items-center justify-center lg:order-2 lg:justify-end">
            <div className="w-full max-w-xl lg:max-w-lg xl:max-w-xl">
              <div className="rounded-[2rem] border border-white/70 bg-white/90 p-6 shadow-[0_30px_80px_-35px_rgba(15,23,42,0.45)] backdrop-blur-xl sm:p-8">
                <div className="mb-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-indigo-600 text-white shadow-lg shadow-primary/20">
                    <LockIcon size={30} />
                  </div>
                  <Typography className="mt-2 text-slate-500">
                    Sign in to continue to your account.
                  </Typography>
                </div>

                {error === "auth" && (
                  <div
                    role="alert"
                    className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  >
                    {tLogin("authenticationError")}
                  </div>
                )}

                <DynamicLoginForm variant="showcase" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function LoginEntryPageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-blue-50 to-indigo-100">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
        <Typography className="mt-4 text-sm font-medium text-slate-500">
          Loading login page...
        </Typography>
      </div>
    </div>
  );
}

export default function LoginEntryPage() {
  return (
    <Suspense fallback={<LoginEntryPageFallback />}>
      <LoginEntryPageContent />
    </Suspense>
  );
}
